import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ImageGeneratorDownloadFormat } from "./imageGeneratorLookExport";

export type DesignSheetDownloadSlide = {
  id: string;
  label: string;
  previewUrl?: string;
};

type Props = {
  open: boolean;
  format: ImageGeneratorDownloadFormat | null;
  slides: DesignSheetDownloadSlide[];
  defaultFileName?: string;
  submitting?: boolean;
  onClose: () => void;
  onDownload: (payload: {
    format: ImageGeneratorDownloadFormat;
    slideIds: string[];
    fileName: string;
  }) => void;
};

const formatLabel = (format: ImageGeneratorDownloadFormat | null) => {
  if (format === "jpeg") {
    return "JPG";
  }
  if (!format) {
    return "";
  }
  return format.toUpperCase();
};

const DownloadCheckbox: React.FC<{
  checked: boolean;
  onChange: () => void;
  label: string;
}> = ({ checked, onChange, label }) => (
  <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-secondary">
    <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="peer absolute inset-0 z-10 m-0 h-full w-full cursor-pointer opacity-0"
      />
      <span
        className={`pointer-events-none flex h-4 w-4 items-center justify-center rounded-[3px] border ${
          checked
            ? "border-[#4D4D4D] bg-[#4D4D4D]"
            : "border-[#737373] bg-white"
        }`}
        aria-hidden="true"
      >
        {checked ? (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path
              d="M1 4.2 3.6 6.8 9 1.2"
              stroke="#FFFFFF"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </span>
    </span>
    <span>{label}</span>
  </label>
);

const ImageGeneratorDesignSheetDownloadModal: React.FC<Props> = ({
  open,
  format,
  slides,
  defaultFileName = "",
  submitting,
  onClose,
  onDownload,
}) => {
  const downloadable = useMemo(
    () => slides.filter(slide => Boolean(slide.previewUrl)),
    [slides]
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [fileName, setFileName] = useState(defaultFileName);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedIds(downloadable.map(slide => slide.id));
    setFileName(defaultFileName);
  }, [open, downloadable, defaultFileName]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, submitting]);

  if (!open || !format) {
    return null;
  }

  const allSelected =
    downloadable.length > 0 &&
    downloadable.every(slide => selectedIds.includes(slide.id));
  const trimmed = fileName.trim();
  const canDownload =
    Boolean(trimmed) &&
    selectedIds.length > 0 &&
    !submitting &&
    Boolean(format);

  const toggleSlide = (id: string) => {
    setSelectedIds(current =>
      current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(downloadable.map(slide => slide.id));
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-[#1F1F1F]/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Select to Download"
      onClick={() => {
        if (!submitting) {
          onClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-[420px] rounded-[16px] bg-white px-5 py-5 shadow-2xl"
        onClick={event => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="font-poppins text-base font-semibold text-secondary">
            Select to Download
          </h2>
          <span className="rounded-full bg-[#F3F3F3] px-2.5 py-0.5 text-[11px] font-medium text-secondary">
            {formatLabel(format)}
          </span>
        </div>

        <div className="mb-5 flex flex-wrap gap-x-5 gap-y-3">
          {downloadable.map((slide, index) => {
            const checked = selectedIds.includes(slide.id);
            return (
              <DownloadCheckbox
                key={slide.id}
                checked={checked}
                onChange={() => toggleSlide(slide.id)}
                label={`Sheet ${index + 1}`}
              />
            );
          })}
        </div>

        <label className="mb-6 block">
          <span className="mb-2 block font-poppins text-[10px] font-normal leading-[135%] tracking-[0.015em] text-[#4D4D4D]">
            File Name
          </span>
          <input
            value={fileName}
            onChange={event => setFileName(event.target.value)}
            placeholder="Name"
            maxLength={120}
            className="h-10 w-full rounded-lg border border-[#D0D0D0] px-3 text-sm font-normal text-[#4D4D4D] placeholder:text-[#4D4D4D] focus:outline-none"
          />
        </label>

        <div className="flex items-center justify-between gap-3">
          <DownloadCheckbox
            checked={allSelected}
            onChange={toggleAll}
            label="Select all"
          />
          <button
            type="button"
            disabled={!canDownload}
            onClick={() =>
              onDownload({
                format,
                slideIds: downloadable
                  .filter(slide => selectedIds.includes(slide.id))
                  .map(slide => slide.id),
                fileName: trimmed,
              })
            }
            className="h-10 shrink-0 rounded-full bg-black px-8 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? "Downloading..." : "Download"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageGeneratorDesignSheetDownloadModal;
